import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import config from '../config';
import './AdminTOSDashboard.css';

/* ─── Constants ──────────────────────────────────────────────────── */
const USERS_PER_PAGE = 10;
const CLIENTS_PER_PAGE = 10;

/* ─── Helpers ────────────────────────────────────────────────────── */
const fmtDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
        const d = new Date(dateStr);
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
    } catch {
        return dateStr;
    }
};

const fmtDateTime = (dateStr) => {
    if (!dateStr) return '—';
    try {
        const d = new Date(dateStr);
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        const h = String(d.getHours()).padStart(2, '0');
        const min = String(d.getMinutes()).padStart(2, '0');
        return `${y}-${m}-${day} ${h}:${min}`;
    } catch {
        return dateStr;
    }
};

/* ─── Custom Dropdown Component ──────────────────────────────────── */
// Supports two modes:
//   Single-select (default): value is a string, onChange(string)
//   Multi-select (multi=true): value is an array, onChange(array)
// When searchable=true a type-in filter input is shown inside the open list.
const CustomDropdown = ({ value, onChange, options, placeholder, searchable = false, multi = false }) => {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [listStyle, setListStyle] = useState({});
    const ref = useRef(null);
    const triggerRef = useRef(null);
    const searchRef = useRef(null);
    const listRef = useRef(null);

    useEffect(() => {
        const handler = (e) => {
            // Also check if click is inside the portal list (which is outside ref.current in the DOM)
            if (
                ref.current && !ref.current.contains(e.target) &&
                listRef.current && !listRef.current.contains(e.target)
            ) {
                setOpen(false);
                setSearch('');
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    // When opening: compute fixed position from trigger's bounding rect
    useEffect(() => {
        if (open && triggerRef.current) {
            const rect = triggerRef.current.getBoundingClientRect();
            const maxH = 320;
            const spaceBelow = window.innerHeight - rect.bottom - 8;
            const spaceAbove = rect.top - 8;
            const openUpward = spaceBelow < maxH && spaceAbove > spaceBelow;
            setListStyle({
                position: 'fixed',
                top: openUpward ? undefined : rect.bottom + 4,
                bottom: openUpward ? (window.innerHeight - rect.top + 4) : undefined,
                left: rect.left,
                width: rect.width,
                zIndex: 99999,
                maxHeight: openUpward ? Math.min(spaceAbove, maxH) : Math.min(spaceBelow, maxH),
            });
        }
        if (open && searchable && searchRef.current) {
            // Delay focus slightly so the element is rendered first
            setTimeout(() => searchRef.current && searchRef.current.focus(), 0);
        }
        if (!open) setSearch('');
    }, [open, searchable]);

    const filteredOptions = searchable && search.trim()
        ? options.filter(opt => opt.toLowerCase().includes(search.toLowerCase()))
        : options;

    // Multi-select helpers
    const isSelected = (opt) => multi ? (Array.isArray(value) && value.includes(opt)) : value === opt;

    const handleSelect = (opt) => {
        if (multi) {
            const current = Array.isArray(value) ? value : [];
            const next = current.includes(opt)
                ? current.filter(v => v !== opt)
                : [...current, opt];
            onChange(next);
            // Keep open for multi-select
        } else {
            onChange(opt);
            setOpen(false);
            setSearch('');
        }
    };

    const handleClear = () => {
        onChange(multi ? [] : '');
        if (!multi) { setOpen(false); setSearch(''); }
    };

    // Trigger display text
    const triggerLabel = multi
        ? (Array.isArray(value) && value.length > 0
            ? value.length === 1 ? value[0].replace(/^spn_/i, '') : `${value.length} SPNs selected`
            : null)
        : (value || null);

    return (
        <div className="tos-custom-dropdown" ref={ref}>
            <button
                ref={triggerRef}
                type="button"
                className={`tos-dropdown-trigger ${open ? 'open' : ''}`}
                onClick={() => setOpen(o => !o)}
            >
                <span className={triggerLabel ? 'tos-dropdown-value' : 'tos-dropdown-placeholder'}>
                    {triggerLabel || placeholder}
                </span>
                <svg className={`tos-dropdown-chevron ${open ? 'rotated' : ''}`} width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
            </button>
            {open && ReactDOM.createPortal(
                <div className="tos-dropdown-list" style={listStyle} ref={listRef}>
                    {/* Type-in filter input */}
                    {searchable && (
                        <div
                            style={{ padding: '8px 10px', borderBottom: '1px solid #2a2a2a' }}
                            onMouseDown={e => e.stopPropagation()}
                        >
                            <input
                                ref={searchRef}
                                type="text"
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                placeholder="Type to filter…"
                                style={{
                                    width: '100%',
                                    padding: '6px 10px',
                                    borderRadius: '6px',
                                    border: '1px solid #3a3a3a',
                                    backgroundColor: '#1a1a1a',
                                    color: '#fff',
                                    fontSize: '13px',
                                    outline: 'none',
                                    boxSizing: 'border-box'
                                }}
                                onMouseDown={e => e.stopPropagation()}
                                onClick={e => e.stopPropagation()}
                                onKeyDown={e => e.key === 'Escape' && setOpen(false)}
                            />
                        </div>
                    )}
                    {/* Clear / placeholder item */}
                    <div
                        className="tos-dropdown-item"
                        onClick={handleClear}
                    >
                        <svg className="tos-dropdown-check" width="14" height="14" viewBox="0 0 14 14" fill="none"
                            style={{ opacity: multi ? 0 : (!value ? 1 : 0) }}>
                            <path d="M2 7l4 4 6-6" stroke="#60A5FA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <span style={{ color: '#888' }}>{placeholder}</span>
                    </div>
                    {filteredOptions.length === 0 ? (
                        <div className="tos-dropdown-item" style={{ color: '#888', cursor: 'default' }}>
                            <span style={{ marginLeft: 22 }}>No results</span>
                        </div>
                    ) : filteredOptions.map(opt => (
                        <div
                            key={opt}
                            className={`tos-dropdown-item ${isSelected(opt) ? 'selected' : ''}`}
                            onClick={() => handleSelect(opt)}
                        >
                            <svg className="tos-dropdown-check" width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ opacity: isSelected(opt) ? 1 : 0 }}>
                                <path d="M2 7l4 4 6-6" stroke="#60A5FA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            <span>{opt}</span>
                        </div>
                    ))}
                </div>,
                document.body
            )}
        </div>
    );
};

/* ─── Pagination Component (Figma-style) ─────────────────────────── */
const Pagination = ({ currentPage, totalPages, onPageChange, totalItems, itemsPerPage, itemLabel }) => {
    if (totalPages <= 1) return null;

    const startItem = Math.min((currentPage - 1) * itemsPerPage + 1, totalItems);
    const endItem = Math.min(currentPage * itemsPerPage, totalItems);

    // Generate page numbers to display
    const getPageNumbers = () => {
        const pages = [];
        if (totalPages <= 7) {
            for (let i = 1; i <= totalPages; i++) pages.push(i);
        } else {
            pages.push(1);
            if (currentPage > 3) pages.push('...');
            const start = Math.max(2, currentPage - 1);
            const end = Math.min(totalPages - 1, currentPage + 1);
            for (let i = start; i <= end; i++) pages.push(i);
            if (currentPage < totalPages - 2) pages.push('...');
            pages.push(totalPages);
        }
        return pages;
    };

    return (
        <div className="tos-table-footer">
            <span className="tos-count-label">
                Showing {startItem}-{endItem} of {totalItems} {itemLabel}
            </span>
            <div className="tos-pagination">
                <button
                    className="tos-page-nav-btn"
                    disabled={currentPage === 1}
                    onClick={() => onPageChange(1)}
                    title="First page"
                >
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                        <path d="M10 2L5 7l5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M6 2L1 7l5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                </button>
                <button
                    className="tos-page-nav-btn"
                    disabled={currentPage === 1}
                    onClick={() => onPageChange(currentPage - 1)}
                >
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                        <path d="M9 2L4 7l5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Prev
                </button>

                {getPageNumbers().map((page, idx) =>
                    page === '...'
                        ? <span key={`dots-${idx}`} className="tos-page-dots">...</span>
                        : <button
                            key={page}
                            className={`tos-page-num ${currentPage === page ? 'active' : ''}`}
                            onClick={() => onPageChange(page)}
                        >
                            {page}
                        </button>
                )}

                <button
                    className="tos-page-nav-btn"
                    disabled={currentPage === totalPages}
                    onClick={() => onPageChange(currentPage + 1)}
                >
                    Next
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                        <path d="M5 2l5 5-5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                </button>
                <button
                    className="tos-page-nav-btn"
                    disabled={currentPage === totalPages}
                    onClick={() => onPageChange(totalPages)}
                    title="Last page"
                >
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                        <path d="M4 2l5 5-5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M8 2l5 5-5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                </button>
            </div>
        </div>
    );
};

/* ─── Component ──────────────────────────────────────────────────── */
const AdminTOSDashboard = () => {
    /* tab: 'clients' | 'users' */
    const [activeTab, setActiveTab] = useState('clients');
    const [currentUser, setCurrentUser] = useState(null);

    /* ── CLIENT STATE ── */
    const [clients, setClients] = useState([]);
    const [clientsLoading, setClientsLoading] = useState(false);
    const [clientSearch, setClientSearch] = useState('');
    const [clientPage, setClientPage] = useState(1);

    /* view: 'list' | 'add' | 'configure' */
    const [view, setView] = useState('list');
    const [allSpns, setAllSpns] = useState([]);
    const [clientSpnsMap, setClientSpnsMap] = useState({}); // clientName → spns[]
    const [clientPowerBIMap, setClientPowerBIMap] = useState({}); // clientName → bool
    const [newClientName, setNewClientName] = useState('');
    const [newClientSpn, setNewClientSpn] = useState('');
    const [addClientError, setAddClientError] = useState('');
    const [addClientLoading, setAddClientLoading] = useState(false);
    const [addClientSuccess, setAddClientSuccess] = useState('');

    /* Configure-Client sub-view */
    const [configureClient, setConfigureClient] = useState(null);
    const [configLoading, setConfigLoading] = useState(false);
    const [configClientName, setConfigClientName] = useState('');  // editable client name
    const [configSpns, setConfigSpns] = useState([]);           // selected SPNs in configure form
    const [configDataStudio, setConfigDataStudio] = useState(false); // PowerBI toggle
    const [configDashboards, setConfigDashboards] = useState([]); // selected dashboard IDs for this client
    const [availableDashboards, setAvailableDashboards] = useState([]); // all dashboards from PowerBI.json
    const [configSaving, setConfigSaving] = useState(false);
    const [configSaveError, setConfigSaveError] = useState('');
    const [configSaveSuccess, setConfigSaveSuccess] = useState('');

    /* ── USER STATE ── */
    const [users, setUsers] = useState([]);
    const [usersLoading, setUsersLoading] = useState(false);
    const [userSearch, setUserSearch] = useState('');
    const [userPage, setUserPage] = useState(1);
    const [userTab, setUserTab] = useState('all'); // 'all' | 'transit' | 'client'
    const [userView, setUserView] = useState('list'); // 'list' | 'add'
    const [userClientsMap, setUserClientsMap] = useState({});
    const [userRoleUpdating, setUserRoleUpdating] = useState(null);
    const [selectedUser, setSelectedUser] = useState(null);

    /* ── ADD USER FORM STATE ── */
    const [newUserName, setNewUserName] = useState('');
    const [newUserEmail, setNewUserEmail] = useState('');
    const [newUserIsTransit, setNewUserIsTransit] = useState(false);
    const [newUserRole, setNewUserRole] = useState('viewer');
    const [newUserRoleOpen, setNewUserRoleOpen] = useState(false);
    const [newUserClientRadio, setNewUserClientRadio] = useState(''); // for client users (single select)
    const [newUserClientChecks, setNewUserClientChecks] = useState([]); // for transit users (multi select)
    const [addUserLoading, setAddUserLoading] = useState(false);
    const [addUserError, setAddUserError] = useState('');
    const [addUserSuccess, setAddUserSuccess] = useState('');
    const newUserRoleRef = useRef(null);

    /* ── EDIT USER FORM STATE ── */
    const [editUserRole, setEditUserRole] = useState('viewer');
    const [editUserRoleOpen, setEditUserRoleOpen] = useState(false);
    const [editUserStatus, setEditUserStatus] = useState('active');
    const [editUserStatusOpen, setEditUserStatusOpen] = useState(false);
    const [editUserIsTransit, setEditUserIsTransit] = useState(false); // persisted transit flag
    const [editUserClients, setEditUserClients] = useState([]); // current client access list
    const [editAddClientValue, setEditAddClientValue] = useState('');
    const [editUserLoading, setEditUserLoading] = useState(false);
    const [editUserError, setEditUserError] = useState('');
    const [editUserSuccess, setEditUserSuccess] = useState('');
    const [editUserDirty, setEditUserDirty] = useState(false);
    const [showDeactivateModal, setShowDeactivateModal] = useState(false);
    const [deactivateLoading, setDeactivateLoading] = useState(false);
    const editUserRoleRef = useRef(null);
    const editUserStatusRef = useRef(null);

    /* ─────────────────────────────────────────────────────────────── */
    /* DATA FETCHING                                                    */
    /* ─────────────────────────────────────────────────────────────── */
    const fetchClients = useCallback(async () => {
        setClientsLoading(true);
        try {
            const res = await axios.get(`${config.apiUrl}/api/admin/clients`);
            setClients(res.data || []);
        } catch (err) {
            console.error('[TOS ADMIN] Error fetching clients:', err);
            setClients([]);
        } finally {
            setClientsLoading(false);
        }
    }, []);

    const fetchAllSpns = useCallback(async () => {
        try {
            const res = await axios.get(`${config.apiUrl}/api/admin/spns`);
            setAllSpns(res.data || []);
        } catch (err) {
            console.error('[TOS ADMIN] Error fetching SPNs:', err);
        }
    }, []);

    const [spnSyncing, setSpnSyncing] = useState(false);
    const handleSyncDatabricksSpns = async () => {
        if (spnSyncing) return;
        setSpnSyncing(true);
        try {
            const response = await axios.post(`${config.apiUrl}/api/admin/fetch-databricks-spns`);
            await fetchAllSpns();
            alert(response.data.message || 'SPNs synced successfully from Databricks.');
        } catch (error) {
            console.error('[TOS ADMIN] Error syncing Databricks SPNs:', error);
            const errorMessage = error.response?.data?.error || error.response?.data?.details || error.message || 'Unknown error';
            alert(`Failed to sync SPNs: ${errorMessage}\n\nCheck browser console for details.`);
        } finally {
            setSpnSyncing(false);
        }
    };

    const fetchClientSpnsMap = useCallback(async () => {
        try {
            const res = await axios.get(`${config.apiUrl}/api/admin/clients-with-spns`);
            const map = {};
            (res.data || []).forEach(item => {
                if (item.name) map[item.name] = item.spns || [];
            });
            setClientSpnsMap(map);
        } catch (err) {
            console.error('[TOS ADMIN] Error fetching client SPNs map:', err);
        }
    }, []);

    const fetchClientPowerBIMap = useCallback(async () => {
        try {
            const res = await axios.get(`${config.apiUrl}/api/admin/clients/powerbi/status`);
            const map = {};
            (res.data || []).forEach(item => {
                if (item.name) map[item.name] = !!item.powerbi_enabled;
            });
            setClientPowerBIMap(map);
        } catch (err) {
            console.error('[TOS ADMIN] Error fetching client PowerBI map:', err);
        }
    }, []);

    const fetchAvailableDashboards = useCallback(async () => {
        try {
            const res = await axios.get(`${config.apiUrl}/api/admin/powerbi/dashboards`);
            setAvailableDashboards(res.data || []);
        } catch (err) {
            console.error('[TOS ADMIN] Error fetching available dashboards:', err);
            setAvailableDashboards([]);
        }
    }, []);

    const fetchClientDashboards = useCallback(async (clientName) => {
        try {
            const res = await axios.get(`${config.apiUrl}/api/admin/clients/${encodeURIComponent(clientName)}/powerbi/dashboards`);
            return res.data.dashboardIds || [];
        } catch (err) {
            console.error('[TOS ADMIN] Error fetching client dashboards:', err);
            return [];
        }
    }, []);

    const fetchAllUserClientsBatch = useCallback(async () => {
        try {
            const res = await axios.get(`${config.apiUrl}/api/admin/users-clients-batch`);
            // res.data is { email: [clientName, ...] }
            setUserClientsMap(prev => ({ ...prev, ...res.data }));
        } catch (err) {
            console.error('[TOS ADMIN] Error fetching user-clients batch:', err);
        }
    }, []);

    const fetchUsers = useCallback(async () => {
        setUsersLoading(true);
        try {
            const PAGE_SIZE = 2000;
            let allUsers = [];
            let page = 1;
            let hasMore = true;
            while (hasMore) {
                const res = await axios.get(`${config.apiUrl}/api/admin/azure-users`, {
                    params: { page, limit: PAGE_SIZE }
                });
                const pageUsers = res.data.users || [];
                allUsers = [...allUsers, ...pageUsers];
                hasMore = res.data.hasMore === true;
                page++;
            }
            // Normalise roles: handle legacy 'super admin' (space) → 'super_admin' (underscore)
            const normalizedUsers = allUsers.map(u => ({
                ...u,
                role: u.role ? u.role.toLowerCase().replace(' ', '_') : u.role
            }));
            setUsers(normalizedUsers);
        } catch (err) {
            console.error('[TOS ADMIN] Error fetching users:', err);
            setUsers([]);
        } finally {
            setUsersLoading(false);
        }
    }, []);

    const fetchUserClientsForPage = useCallback(async (visibleUsers) => {
        const emailsToFetch = visibleUsers
            .map(u => u.mail || u.userPrincipalName)
            .filter(e => !(e in userClientsMap));
        if (emailsToFetch.length === 0) return;
        const results = await Promise.all(
            emailsToFetch.map(email =>
                axios.get(`${config.apiUrl}/api/admin/users/${email}/clients`)
                    .then(r => ({ email, clients: r.data.clients || [] }))
                    .catch(() => ({ email, clients: [] }))
            )
        );
        setUserClientsMap(prev => {
            const next = { ...prev };
            results.forEach(({ email, clients }) => { next[email] = clients; });
            return next;
        });
    }, [userClientsMap]);

    const handleOpenConfigure = async (client) => {
        setConfigureClient(client);
        setConfigClientName(client.name);
        setConfigSpns(clientSpnsMap[client.name] || []);
        setConfigDataStudio(clientPowerBIMap[client.name] === true);
        setConfigSaveError('');
        setConfigSaveSuccess('');
        setConfigLoading(true);
        setView('configure');
        try {
            const [dashboards] = await Promise.all([
                fetchClientDashboards(client.name),
                availableDashboards.length === 0 ? fetchAvailableDashboards() : Promise.resolve()
            ]);
            setConfigDashboards(dashboards);
        } catch (err) {
            console.error('[TOS ADMIN] Error loading configure data:', err);
        } finally {
            setConfigLoading(false);
        }
    };

    const handleSaveConfigure = async () => {
        if (!configureClient) return;
        const trimmedName = configClientName.trim();
        if (!trimmedName) {
            setConfigSaveError('Client name cannot be empty.');
            return;
        }
        setConfigSaving(true);
        setConfigSaveError('');
        setConfigSaveSuccess('');
        try {
            const originalName = configureClient.name;
            const nameChanged = trimmedName !== originalName;

            // 1. Rename client if name changed
            if (nameChanged) {
                await axios.put(
                    `${config.apiUrl}/api/admin/clients/${encodeURIComponent(originalName)}`,
                    { name: trimmedName }
                );
            }

            // Use the (possibly updated) name for subsequent calls
            const effectiveName = nameChanged ? trimmedName : originalName;

            // 2. Update SPNs
            await axios.put(
                `${config.apiUrl}/api/admin/clients/${encodeURIComponent(effectiveName)}/spns`,
                { spns: configSpns }
            );
            // 3. Update PowerBI / Data Studio toggle
            const endpoint = configDataStudio ? 'enable' : 'disable';
            await axios.post(
                `${config.apiUrl}/api/admin/clients/${encodeURIComponent(effectiveName)}/powerbi/${endpoint}`
            );
            // 4. Update PowerBI dashboard mappings
            await axios.put(
                `${config.apiUrl}/api/admin/clients/${encodeURIComponent(effectiveName)}/powerbi/dashboards`,
                { dashboardIds: configDashboards }
            );
            // 5. Refresh maps + clients list
            await fetchClients();
            await fetchClientSpnsMap();
            await fetchClientPowerBIMap();

            // 6. Update configureClient reference so back-to-list is clean
            if (nameChanged) {
                setConfigureClient(prev => ({ ...prev, name: trimmedName }));
            }

            setConfigSaveSuccess('Configuration saved successfully.');
        } catch (err) {
            console.error('[TOS ADMIN] Error saving configure:', err);
            setConfigSaveError(err?.response?.data?.error || 'Failed to save configuration.');
        } finally {
            setConfigSaving(false);
        }
    };

    useEffect(() => {
        // Fetch current logged-in user's role
        axios.get(`${config.apiUrl}/api/user`)
            .then(res => {
                const data = res.data;
                // Normalise role: handle legacy 'super admin' (space) → 'super_admin' (underscore)
                if (data && data.role) data.role = data.role.toLowerCase().replace(' ', '_');
                setCurrentUser(data);
            })
            .catch(() => setCurrentUser(null));
        fetchClients();
        fetchAllSpns();
        fetchClientSpnsMap();
        fetchClientPowerBIMap();
        fetchAvailableDashboards();
        fetchAllUserClientsBatch();
    }, [fetchClients, fetchAllSpns, fetchClientSpnsMap, fetchClientPowerBIMap, fetchAvailableDashboards, fetchAllUserClientsBatch]);
    useEffect(() => { if (activeTab === 'users') fetchUsers(); }, [activeTab, fetchUsers]);

    /* ─────────────────────────────────────────────────────────────── */
    /* CLIENTS LOGIC                                                    */
    /* ─────────────────────────────────────────────────────────────── */
    const clientUserCounts = React.useMemo(() => {
        const counts = {};
        Object.entries(userClientsMap).forEach(([, clientList]) => {
            (Array.isArray(clientList) ? clientList : []).forEach(name => {
                counts[name] = (counts[name] || 0) + 1;
            });
        });
        return counts;
    }, [userClientsMap]);

    const filteredClients = clients.filter(c =>
        c.name?.toLowerCase().includes(clientSearch.toLowerCase()) ||
        (c.spn || '').toLowerCase().includes(clientSearch.toLowerCase())
    );
    const totalClientPages = Math.max(1, Math.ceil(filteredClients.length / CLIENTS_PER_PAGE));
    const pagedClients = filteredClients.slice(
        (clientPage - 1) * CLIENTS_PER_PAGE,
        clientPage * CLIENTS_PER_PAGE
    );

    const getClientApps = (client) => {
        const apps = [];
        if (clientPowerBIMap[client.name] === true) {
            apps.push('Data Studio');
        }
        apps.push('AI Studio');
        return apps;
    };

    const getClientStatus = (client) => {
        if (client.status) return client.status;
        return client.active === false ? 'Inactive' : 'Active';
    };

    const handleCreateClient = async (e) => {
        e.preventDefault();
        if (!newClientName.trim()) { setAddClientError('Client Name is required'); return; }
        setAddClientLoading(true);
        setAddClientError('');
        setAddClientSuccess('');
        try {
            await axios.post(`${config.apiUrl}/api/admin/clients`, {
                name: newClientName.trim(),
                spn: newClientSpn || undefined
            });
            // If an SPN was selected, assign it to the client via the client-spn-mappings endpoint
            if (newClientSpn) {
                try {
                    await axios.post(`${config.apiUrl}/api/admin/clients/${encodeURIComponent(newClientName.trim())}/spns`, {
                        spnName: newClientSpn
                    });
                } catch (spnErr) {
                    console.warn('[TOS ADMIN] Could not assign SPN to client:', spnErr);
                }
            }
            setAddClientSuccess(`Client "${newClientName}" created successfully!`);
            setNewClientName('');
            setNewClientSpn('');
            await fetchClients();
            await fetchClientSpnsMap();
            setTimeout(() => { setView('list'); setAddClientSuccess(''); }, 1800);
        } catch (err) {
            setAddClientError(err.response?.data?.details || err.message || 'Failed to create client');
        } finally {
            setAddClientLoading(false);
        }
    };


    /* ─────────────────────────────────────────────────────────────── */
    /* USERS LOGIC                                                      */
    /* ─────────────────────────────────────────────────────────────── */

    // Determine user type: prefer the persisted is_transit flag from the DB.
    // Fall back to email-domain heuristic only if the flag is not present (legacy records).
    const getUserType = (user) => {
        if (user.is_transit === true) return 'transit';
        const email = (user.mail || user.userPrincipalName || '').toLowerCase();
        if (email.includes('@transit-technologies.com') || email.includes('@transittech')) return 'transit';
        return 'client';
    };

    const allFilteredUsers = users.filter(u =>
        (u.displayName || '').toLowerCase().includes(userSearch.toLowerCase()) ||
        (u.mail || u.userPrincipalName || '').toLowerCase().includes(userSearch.toLowerCase())
    );

    const transitUsers = allFilteredUsers.filter(u => getUserType(u) === 'transit');
    const clientUsers = allFilteredUsers.filter(u => getUserType(u) === 'client');

    const tabUsers = userTab === 'transit' ? transitUsers
        : userTab === 'client' ? clientUsers
        : allFilteredUsers;

    const totalUserPages = Math.max(1, Math.ceil(tabUsers.length / USERS_PER_PAGE));
    const pagedUsers = tabUsers.slice(
        (userPage - 1) * USERS_PER_PAGE,
        userPage * USERS_PER_PAGE
    );

    useEffect(() => {
        if (pagedUsers.length > 0) {
            fetchUserClientsForPage(pagedUsers);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userPage, userSearch, userTab, users]);

    // eslint-disable-next-line no-unused-vars
    const handleUpdateRole = async (user, newRole) => {
        const email = user.mail || user.userPrincipalName;
        setUserRoleUpdating(email);
        try {
            await axios.post(`${config.apiUrl}/api/admin/update-user-role`, { email, role: newRole });
            setUsers(prev => prev.map(u =>
                (u.mail || u.userPrincipalName) === email ? { ...u, role: newRole } : u
            ));
        } catch (err) {
            alert(`Failed to update role: ${err.response?.data?.details || err.message}`);
        } finally {
            setUserRoleUpdating(null);
        }
    };

    const getRoleBadgeClass = (role) => {
        switch ((role || '').toLowerCase()) {
            case 'super_admin': return 'tos-role-badge super-admin';
            case 'admin': return 'tos-role-badge admin';
            default: return 'tos-role-badge viewer';
        }
    };

    const getRoleLabel = (role) => {
        if (!role) return 'Viewer';
        if (role.toLowerCase() === 'super_admin') return 'Super Admin';
        return role.charAt(0).toUpperCase() + role.slice(1);
    };

    /* ─────────────────────────────────────────────────────────────── */
    /* ADD USER LOGIC                                                   */
    /* ─────────────────────────────────────────────────────────────── */
    const resetAddUserForm = () => {
        setNewUserName('');
        setNewUserEmail('');
        setNewUserIsTransit(false);
        setNewUserRole('viewer');
        setNewUserRoleOpen(false);
        setNewUserClientRadio('');
        setNewUserClientChecks([]);
        setAddUserError('');
        setAddUserSuccess('');
    };

    const handleCreateUser = async (e) => {
        e.preventDefault();
        if (!newUserName.trim()) { setAddUserError('Full Name is required'); return; }
        if (!newUserEmail.trim()) { setAddUserError('Email Address is required'); return; }
        if (!newUserIsTransit && !newUserClientRadio) { setAddUserError('Please select a client'); return; }
        setAddUserLoading(true);
        setAddUserError('');
        setAddUserSuccess('');
        try {
            // 1. Create user in TTAuth + local DB (pass isTransit so it's persisted immediately)
            await axios.post(`${config.apiUrl}/api/admin/create-azure-user`, {
                displayName: newUserName.trim(),
                userPrincipalName: newUserEmail.trim(),
                isTransit: newUserIsTransit
            });
            // 2. Set role (user now exists in DB)
            if (newUserRole && newUserRole !== 'viewer') {
                await axios.post(`${config.apiUrl}/api/admin/update-user-role`, {
                    email: newUserEmail.trim(),
                    role: newUserRole
                });
            }
            // 3. Assign clients
            const clientsToAssign = newUserIsTransit ? newUserClientChecks : (newUserClientRadio ? [newUserClientRadio] : []);
            if (clientsToAssign.length > 0) {
                await axios.post(`${config.apiUrl}/api/admin/users/${newUserEmail.trim()}/clients`, {
                    clients: clientsToAssign
                });
            }
            setAddUserSuccess(`User "${newUserName}" added successfully!`);
            await fetchUsers();
            setTimeout(() => {
                setUserView('list');
                resetAddUserForm();
            }, 1800);
        } catch (err) {
            setAddUserError(err.response?.data?.details || err.response?.data?.error || err.message || 'Failed to add user');
        } finally {
            setAddUserLoading(false);
        }
    };

    // Role options for the dropdown (Screens 13-15)
    // Super Admin option only visible to users who are already super_admin
    // Handle both 'super_admin' (underscore) and 'super admin' (space) for backward compat
    const isSuperAdminUser = ['super_admin', 'super admin'].includes((currentUser?.role || '').toLowerCase());
    const isAdminUser = (currentUser?.role || '').toLowerCase() === 'admin';
    const canEditUsers = isSuperAdminUser || isAdminUser; // both admin and super_admin can create/edit users
    const USER_ROLE_OPTIONS = [
        { value: 'viewer', label: 'Viewer', desc: 'Read-only access' },
        { value: 'admin', label: 'Admin', desc: 'Can manage users' },
        ...(isSuperAdminUser
            ? [{ value: 'super_admin', label: 'Super Admin', desc: 'Can manage clients, applications, features, users' }]
            : []),
    ];

    // Close role dropdown on outside click
    useEffect(() => {
        if (!newUserRoleOpen) return;
        const handler = (e) => {
            if (newUserRoleRef.current && !newUserRoleRef.current.contains(e.target)) {
                setNewUserRoleOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [newUserRoleOpen]);

    const toggleClientCheck = (clientName) => {
        setNewUserClientChecks(prev =>
            prev.includes(clientName)
                ? prev.filter(c => c !== clientName)
                : [...prev, clientName]
        );
    };

    const selectedRoleOption = USER_ROLE_OPTIONS.find(r => r.value === newUserRole) || USER_ROLE_OPTIONS[0];
    const isAddUserFormValid = newUserName.trim() && newUserEmail.trim() &&
        (newUserIsTransit ? true : !!newUserClientRadio);

    /* ─────────────────────────────────────────────────────────────── */
    /* VIEWS                                                            */
    /* ─────────────────────────────────────────────────────────────── */

    /* ── Edit User helpers ── */
    const USER_STATUS_OPTIONS = [
        { value: 'active', label: 'Active' },
        { value: 'inactive', label: 'Inactive' },
    ];

    const openEditUser = (user) => {
        const email = user.mail || user.userPrincipalName;
        setSelectedUser(user);
        setEditUserRole((user.role || 'viewer').toLowerCase());
        // Initialise status from the is_active flag returned by the API
        setEditUserStatus(user.is_active === false ? 'inactive' : 'active');
        // Initialise transit flag from the DB field (falls back to email-domain heuristic via getUserType)
        setEditUserIsTransit(user.is_transit === true || getUserType(user) === 'transit');
        setEditUserClients(userClientsMap[email] ? [...userClientsMap[email]] : []);
        setEditUserDirty(false);
        setEditUserError('');
        setEditUserSuccess('');
        setEditUserRoleOpen(false);
        setEditUserStatusOpen(false);
        setShowDeactivateModal(false);
        setUserView('edit');
    };

    const handleSaveUserChanges = async () => {
        if (!selectedUser) return;
        setEditUserLoading(true);
        setEditUserError('');
        setEditUserSuccess('');
        try {
            const email = selectedUser.mail || selectedUser.userPrincipalName;
            console.log('[TOS ADMIN] Saving user changes:', { email, role: editUserRole, status: editUserStatus, isTransit: editUserIsTransit, clients: editUserClients });

            if (!email) {
                setEditUserError('Cannot save: user email is missing. Please close and re-open this edit view.');
                return;
            }

            // Update role
            await axios.post(`${config.apiUrl}/api/admin/update-user-role`, {
                email,
                role: editUserRole || 'viewer',
            });

            // Persist transit flag if it changed
            const originalIsTransit = selectedUser.is_transit === true || getUserType(selectedUser) === 'transit';
            if (editUserIsTransit !== originalIsTransit) {
                await axios.post(`${config.apiUrl}/api/admin/update-user-transit`, {
                    email,
                    isTransit: editUserIsTransit,
                });
            }

            // Handle status change via deactivate/activate endpoints
            const currentIsActive = selectedUser.is_active !== false; // true if active or undefined
            const wantsActive = editUserStatus !== 'inactive';
            if (wantsActive && !currentIsActive) {
                // User changed status from inactive → active
                await axios.post(`${config.apiUrl}/api/admin/activate-user`, { email });
            } else if (!wantsActive && currentIsActive) {
                // User changed status from active → inactive
                await axios.post(`${config.apiUrl}/api/admin/deactivate-user`, { email });
            }

            // Update client access (replace all — uses PUT which accepts a clients array)
            await axios.put(`${config.apiUrl}/api/admin/users/${email}/clients`, {
                clients: editUserClients,
            });

            setEditUserSuccess('Changes saved successfully.');
            setEditUserDirty(false);

            // Refresh users list, then re-apply known is_active / is_transit state to avoid stale API data
            await fetchUsers();
            const newIsActive = wantsActive;
            setUsers(prev => prev.map(u =>
                (u.mail || u.userPrincipalName) === email
                    ? { ...u, is_active: newIsActive, is_transit: editUserIsTransit }
                    : u
            ));
            setUserClientsMap(prev => ({ ...prev, [email]: editUserClients }));
        } catch (err) {
            console.error('[TOS ADMIN] Save user error:', err);
            const serverMsg = err?.response?.data?.error || err?.response?.data?.details || '';
            setEditUserError(`Failed to save changes.${serverMsg ? ` Server: ${serverMsg}` : ' Please try again.'}`);
        } finally {
            setEditUserLoading(false);
        }
    };

    const handleDeactivateUser = async () => {
        if (!selectedUser) return;
        setDeactivateLoading(true);
        try {
            const email = selectedUser.mail || selectedUser.userPrincipalName;
            await axios.post(`${config.apiUrl}/api/admin/deactivate-user`, { email });
            setShowDeactivateModal(false);
            // Refresh from server, then force is_active=false for this user in case
            // the DB column doesn't exist yet and the API returns stale isActive:true.
            await fetchUsers();
            setUsers(prev => prev.map(u =>
                (u.mail || u.userPrincipalName) === email ? { ...u, is_active: false } : u
            ));
            setUserView('list');
        } catch (err) {
            console.error('[TOS ADMIN] Deactivate user error:', err);
            setShowDeactivateModal(false);
        } finally {
            setDeactivateLoading(false);
        }
    };

    const handleActivateUser = async () => {
        if (!selectedUser) return;
        setDeactivateLoading(true);
        try {
            const email = selectedUser.mail || selectedUser.userPrincipalName;
            await axios.post(`${config.apiUrl}/api/admin/activate-user`, { email });
            setShowDeactivateModal(false);
            // Refresh from server, then force is_active=true for this user in case
            // the DB column doesn't exist yet and the API returns stale isActive:false.
            await fetchUsers();
            setUsers(prev => prev.map(u =>
                (u.mail || u.userPrincipalName) === email ? { ...u, is_active: true } : u
            ));
            setUserView('list');
        } catch (err) {
            console.error('[TOS ADMIN] Activate user error:', err);
            setShowDeactivateModal(false);
        } finally {
            setDeactivateLoading(false);
        }
    };

    /* ── Edit User Sub-view (Screens 16, 18, 20, 21) ── */
    const renderEditUserView = () => {
        if (!selectedUser) return null;
        const displayName = selectedUser.displayName || 'User';
        const email = selectedUser.mail || selectedUser.userPrincipalName;

        // Clients available to add (not already in the list)
        const availableToAdd = clients.filter(c => !editUserClients.includes(c.name));
        const selectedEditRoleOption = USER_ROLE_OPTIONS.find(r => r.value === editUserRole) || USER_ROLE_OPTIONS[0];
        const selectedStatusOption = USER_STATUS_OPTIONS.find(s => s.value === editUserStatus) || USER_STATUS_OPTIONS[0];

        return (
            <div className="tos-subview tos-edit-user-view">
                {/* Back */}
                <button
                    className="tos-back-btn"
                    onClick={() => { setUserView('list'); setSelectedUser(null); }}
                >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                        <path d="M19 12H5M5 12L12 19M5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Back
                </button>

                {/* Header — transit badge is a clickable toggle; change is persisted on Save */}
                <div className="tos-eu-header">
                    <h2 className="tos-subview-title">{displayName}</h2>
                    <button
                        type="button"
                        className={`tos-type-badge ${editUserIsTransit ? 'transit' : 'client'}`}
                        title={editUserIsTransit ? 'Click to change to Client User' : 'Click to change to Transit Internal'}
                        style={{ cursor: 'pointer', background: 'none', border: 'none', padding: 0 }}
                        onClick={() => { setEditUserIsTransit(v => !v); setEditUserDirty(true); }}
                    >
                        {editUserIsTransit ? 'Transit Internal' : 'Client User'}
                    </button>
                </div>
                <p className="tos-subview-subtitle">
                    {editUserIsTransit
                        ? 'Transit Tech internal user with multi-client access'
                        : 'Client user with single-client access'}
                </p>

                {editUserError && <div className="tos-form-error" style={{ marginBottom: 16 }}>{editUserError}</div>}
                {editUserSuccess && <div className="tos-form-success" style={{ marginBottom: 16 }}>{editUserSuccess}</div>}

                {/* Basic Information */}
                <div className="tos-au-section">
                    <div className="tos-au-section-header">
                        <div className="tos-au-section-title">Basic Information</div>
                    </div>
                    <div className="tos-au-section-body">
                        <div className="tos-au-two-col">
                            <div className="tos-form-group" style={{ marginBottom: 0 }}>
                                <label className="tos-label">Full Name</label>
                                <div className="tos-input tos-input-readonly">{displayName}</div>
                            </div>
                            <div className="tos-form-group" style={{ marginBottom: 0 }}>
                                <label className="tos-label">Email Address</label>
                                <div className="tos-input tos-input-readonly">{email}</div>
                            </div>
                        </div>

                        {/* Transit Internal toggle */}
                        <div className="tos-au-toggle-row" style={{ marginTop: 16 }}>
                            <button
                                type="button"
                                className={`tos-au-toggle ${editUserIsTransit ? 'on' : 'off'}`}
                                onClick={() => { setEditUserIsTransit(v => !v); setEditUserDirty(true); }}
                                aria-pressed={editUserIsTransit}
                            >
                                <span className="tos-au-toggle-knob" />
                            </button>
                            <div className="tos-au-toggle-label">
                                <span className="tos-au-toggle-title">Transit Internal User</span>
                                <span className="tos-au-toggle-desc">When enabled, this user can access multiple client environments and will NOT appear in client admin user lists. Use for Transit Tech employees.</span>
                            </div>
                        </div>

                        {/* Role */}
                        <div className="tos-form-group" style={{ marginTop: 16, marginBottom: 0 }}>
                            <label className="tos-label">Role</label>
                            <div className="tos-custom-dropdown" ref={editUserRoleRef} style={{ position: 'relative' }}>
                                <button
                                    type="button"
                                    className={`tos-dropdown-trigger ${editUserRoleOpen ? 'open' : ''}`}
                                    onClick={() => { setEditUserRoleOpen(o => !o); setEditUserStatusOpen(false); }}
                                >
                                    <span className="tos-dropdown-value">
                                        {selectedEditRoleOption.label} — {selectedEditRoleOption.desc}
                                    </span>
                                    <svg className={`tos-dropdown-chevron ${editUserRoleOpen ? 'rotated' : ''}`} width="16" height="16" viewBox="0 0 16 16" fill="none">
                                        <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                </button>
                                {editUserRoleOpen && (
                                    <div className="tos-dropdown-list">
                                        {USER_ROLE_OPTIONS.map(opt => (
                                            <div
                                                key={opt.value}
                                                className={`tos-dropdown-item ${editUserRole === opt.value ? 'selected' : ''}`}
                                                onClick={() => { setEditUserRole(opt.value); setEditUserRoleOpen(false); setEditUserDirty(true); }}
                                            >
                                                <svg className="tos-dropdown-check" width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ opacity: editUserRole === opt.value ? 1 : 0 }}>
                                                    <path d="M2 7l4 4 6-6" stroke="#60A5FA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                </svg>
                                                <span>
                                                    <span style={{ fontWeight: 600 }}>{opt.label}</span>
                                                    <span style={{ color: '#777', marginLeft: 8 }}>— {opt.desc}</span>
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Status */}
                        <div className="tos-form-group" style={{ marginTop: 16, marginBottom: 0 }}>
                            <label className="tos-label">Status</label>
                            <div className="tos-custom-dropdown" ref={editUserStatusRef} style={{ position: 'relative' }}>
                                <button
                                    type="button"
                                    className={`tos-dropdown-trigger ${editUserStatusOpen ? 'open' : ''}`}
                                    onClick={() => { setEditUserStatusOpen(o => !o); setEditUserRoleOpen(false); }}
                                >
                                    <span className="tos-dropdown-value">{selectedStatusOption.label}</span>
                                    <svg className={`tos-dropdown-chevron ${editUserStatusOpen ? 'rotated' : ''}`} width="16" height="16" viewBox="0 0 16 16" fill="none">
                                        <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                </button>
                                {editUserStatusOpen && (
                                    <div className="tos-dropdown-list">
                                        {USER_STATUS_OPTIONS.map(opt => (
                                            <div
                                                key={opt.value}
                                                className={`tos-dropdown-item ${editUserStatus === opt.value ? 'selected' : ''}`}
                                                onClick={() => { setEditUserStatus(opt.value); setEditUserStatusOpen(false); setEditUserDirty(true); }}
                                            >
                                                <svg className="tos-dropdown-check" width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ opacity: editUserStatus === opt.value ? 1 : 0 }}>
                                                    <path d="M2 7l4 4 6-6" stroke="#60A5FA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                </svg>
                                                <span>{opt.label}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Client Access & Groups */}
                <div className="tos-au-section">
                    <div className="tos-au-section-header">
                        <div className="tos-au-section-title">Client Access & Groups</div>
                        <div className="tos-au-section-desc">
                            {editUserIsTransit
                                ? 'Transit Internal users can access multiple client environments. They do not appear in client admin user lists.'
                                : 'This client user belongs to one client environment.'}
                        </div>
                    </div>
                    <div className="tos-au-section-body">
                        {/* Current client list with Remove buttons */}
                        {editUserClients.length === 0 ? (
                            <div className="tos-none-label" style={{ padding: '8px 0' }}>No client access assigned.</div>
                        ) : editUserClients.map(clientName => (
                            <div key={clientName} className="tos-eu-client-row">
                                <div className="tos-eu-client-info">
                                    <div className="tos-eu-client-name">{clientName}</div>
                                    <div className="tos-eu-client-desc">Can emulate client experience</div>
                                </div>
                                <button
                                    type="button"
                                    className="tos-eu-remove-btn"
                                    onClick={() => {
                                        setEditUserClients(prev => prev.filter(c => c !== clientName));
                                        setEditUserDirty(true);
                                    }}
                                >
                                    Remove
                                </button>
                            </div>
                        ))}

                        {/* Add Access dropdown */}
                        <div className="tos-eu-add-access-wrap">
                            <div className="tos-eu-add-label">Add Access To Another Client Environment</div>
                            <CustomDropdown
                                value={editAddClientValue}
                                onChange={(selected) => {
                                    if (selected) {
                                        setEditUserClients(prev => [...prev, selected]);
                                        setEditUserDirty(true);
                                        setEditAddClientValue('');
                                    }
                                }}
                                options={availableToAdd.map(c => c.name)}
                                placeholder={availableToAdd.length === 0 ? 'All clients already added' : 'Select client to add'}
                                searchable={true}
                            />
                        </div>
                    </div>
                </div>

                {/* Footer actions */}
                <div className="tos-eu-footer">
                    {selectedUser?.is_active === false ? (
                        <button
                            type="button"
                            className="tos-eu-activate-btn"
                            onClick={() => setShowDeactivateModal(true)}
                        >
                            Activate User
                        </button>
                    ) : (
                        <button
                            type="button"
                            className="tos-eu-deactivate-btn"
                            onClick={() => setShowDeactivateModal(true)}
                        >
                            Deactivate User
                        </button>
                    )}
                    <div style={{ display: 'flex', gap: 12 }}>
                        <button
                            type="button"
                            className="tos-btn-cancel"
                            onClick={() => { setUserView('list'); setSelectedUser(null); }}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            className={`tos-btn-create ${editUserDirty ? 'valid' : ''}`}
                            disabled={!editUserDirty || editUserLoading}
                            onClick={handleSaveUserChanges}
                        >
                            {editUserLoading ? 'Saving…' : 'Save Changes'}
                        </button>
                    </div>
                </div>

                {/* Deactivate / Activate Confirmation Modal */}
                {showDeactivateModal && (
                    <div className="tos-deactivate-overlay">
                        <div className="tos-deactivate-dialog">
                            {selectedUser?.is_active === false ? (
                                <>
                                    <h3 className="tos-deactivate-title">Activate User</h3>
                                    <p className="tos-deactivate-subtitle">
                                        Are you sure you want to activate {displayName}?
                                    </p>
                                    <div className="tos-deactivate-warning" style={{ borderColor: 'rgba(34,197,94,0.3)', background: 'rgba(34,197,94,0.07)' }}>
                                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0, marginTop: 1 }}>
                                            <circle cx="8" cy="8" r="7" fill="#22C55E" fillOpacity="0.15"/>
                                            <circle cx="8" cy="8" r="7" stroke="#22C55E" strokeWidth="1.5"/>
                                            <path d="M5 8l2 2 4-4" stroke="#22C55E" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                        <span style={{ color: '#22C55E' }}>
                                            This user will regain access and be able to log in again.
                                        </span>
                                    </div>
                                    <div className="tos-deactivate-actions">
                                        <button
                                            className="tos-btn-cancel"
                                            onClick={() => setShowDeactivateModal(false)}
                                            disabled={deactivateLoading}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            className="tos-eu-activate-confirm-btn"
                                            onClick={handleActivateUser}
                                            disabled={deactivateLoading}
                                        >
                                            {deactivateLoading ? 'Activating…' : 'Activate User'}
                                        </button>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <h3 className="tos-deactivate-title">Deactivate User</h3>
                                    <p className="tos-deactivate-subtitle">
                                        Are you sure you want to deactivate {displayName}?
                                    </p>
                                    <div className="tos-deactivate-warning">
                                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0, marginTop: 1 }}>
                                            <circle cx="8" cy="8" r="7" fill="#EF4444" fillOpacity="0.15"/>
                                            <circle cx="8" cy="8" r="7" stroke="#EF4444" strokeWidth="1.5"/>
                                            <path d="M8 5v3.5M8 11h.01" stroke="#EF4444" strokeWidth="1.5" strokeLinecap="round"/>
                                        </svg>
                                        <span>
                                            This {editUserIsTransit ? 'Transit Internal' : 'Client'} user will lose access to all client environments and will no longer be able to log in.
                                        </span>
                                    </div>
                                    <div className="tos-deactivate-actions">
                                        <button
                                            className="tos-btn-cancel"
                                            onClick={() => setShowDeactivateModal(false)}
                                            disabled={deactivateLoading}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            className="tos-eu-deactivate-confirm-btn"
                                            onClick={handleDeactivateUser}
                                            disabled={deactivateLoading}
                                        >
                                            {deactivateLoading ? 'Deactivating…' : 'Deactivate User'}
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    /* ── Add User Sub-view (Screens 11-15) ── */
    const renderAddUserView = () => (
        <div className="tos-subview tos-add-user-view">
            <button
                className="tos-back-btn"
                onClick={() => { setUserView('list'); resetAddUserForm(); }}
            >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M19 12H5M5 12L12 19M5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Back
            </button>

            <h2 className="tos-subview-title">Add User</h2>
            <p className="tos-subview-subtitle">
                Add a new user to the system. Transit Internal users can access multiple clients and have role assignment configured here. Client users belong to one client and have role/group assignment managed by client admins.
            </p>

            <form onSubmit={handleCreateUser}>
                {addUserError && <div className="tos-form-error" style={{ marginBottom: 16 }}>{addUserError}</div>}
                {addUserSuccess && <div className="tos-form-success" style={{ marginBottom: 16 }}>{addUserSuccess}</div>}

                {/* ── Section 1: Application Access ── */}
                <div className="tos-au-section">
                    <div className="tos-au-section-header">
                        <div className="tos-au-section-title">Application Access</div>
                        <div className="tos-au-section-desc">Applications licensed for this client. Controls the visibility on LaunchPad.</div>
                    </div>
                    <div className="tos-au-section-body">
                        <div className="tos-au-toggle-row">
                            {/* Toggle switch */}
                            <button
                                type="button"
                                className={`tos-au-toggle ${newUserIsTransit ? 'on' : 'off'}`}
                                onClick={() => {
                                    setNewUserIsTransit(p => !p);
                                    setNewUserClientRadio('');
                                    setNewUserClientChecks([]);
                                    setNewUserRole('viewer');
                                }}
                                aria-label="Toggle Transit Internal"
                            >
                                <span className="tos-au-toggle-knob" />
                            </button>
                            <div className="tos-au-toggle-label">
                                <span className="tos-au-toggle-title">Mark "Transit Internal" User</span>
                                <span className="tos-au-toggle-desc">When enabled, this user can access multiple client environments and will NOT appear in client admin user lists. Use for Transit Tech employees who need to emulate client experiences.</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ── Section 2: Basic Information ── */}
                <div className="tos-au-section">
                    <div className="tos-au-section-header">
                        <div className="tos-au-section-title">Basic Information</div>
                        <div className="tos-required-note" style={{ fontSize: 12, marginTop: 4 }}>Item with an asterisk(*) is required.</div>
                    </div>
                    <div className="tos-au-section-body">
                        <div className="tos-au-two-col">
                            <div className="tos-form-group" style={{ marginBottom: 0 }}>
                                <label className="tos-label">Full Name <span className="tos-asterisk">*</span></label>
                                <input
                                    className="tos-input"
                                    type="text"
                                    placeholder="Enter full name"
                                    value={newUserName}
                                    onChange={e => setNewUserName(e.target.value)}
                                    required
                                />
                            </div>
                            <div className="tos-form-group" style={{ marginBottom: 0 }}>
                                <label className="tos-label">Email Address <span className="tos-asterisk">*</span></label>
                                <input
                                    className="tos-input"
                                    type="email"
                                    placeholder="user@example.com"
                                    value={newUserEmail}
                                    onChange={e => setNewUserEmail(e.target.value)}
                                    required
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* ── Section 3: Role Assignment ── */}
                <div className="tos-au-section">
                    <div className="tos-au-section-header">
                        <div className="tos-au-section-title">Role Assignment</div>
                        <div className="tos-au-section-desc">
                            {newUserIsTransit
                                ? 'Assign the role for this Transit Internal user. Roles determine access levels across all client environments.'
                                : 'Client users will be created with default "Viewer" role. Client admins can assign roles and groups to manage this user\'s access to features and applications.'}
                        </div>
                    </div>
                    {newUserIsTransit && (
                        <div className="tos-au-section-body">
                            <div className="tos-form-group" style={{ marginBottom: 0 }}>
                                <label className="tos-label">User Role <span className="tos-asterisk">*</span></label>
                                <div className="tos-custom-dropdown" ref={newUserRoleRef} style={{ position: 'relative' }}>
                                    <button
                                        type="button"
                                        className={`tos-dropdown-trigger ${newUserRoleOpen ? 'open' : ''}`}
                                        onClick={() => setNewUserRoleOpen(o => !o)}
                                    >
                                        <span className="tos-dropdown-value">
                                            {selectedRoleOption.label} — {selectedRoleOption.desc}
                                        </span>
                                        <svg className={`tos-dropdown-chevron ${newUserRoleOpen ? 'rotated' : ''}`} width="16" height="16" viewBox="0 0 16 16" fill="none">
                                            <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                    </button>
                                    {newUserRoleOpen && (
                                        <div className="tos-dropdown-list">
                                            {USER_ROLE_OPTIONS.map(opt => (
                                                <div
                                                    key={opt.value}
                                                    className={`tos-dropdown-item ${newUserRole === opt.value ? 'selected' : ''}`}
                                                    onClick={() => { setNewUserRole(opt.value); setNewUserRoleOpen(false); }}
                                                >
                                                    <svg className="tos-dropdown-check" width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ opacity: newUserRole === opt.value ? 1 : 0 }}>
                                                        <path d="M2 7l4 4 6-6" stroke="#60A5FA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                    </svg>
                                                    <span>
                                                        <span style={{ fontWeight: 600 }}>{opt.label}</span>
                                                        <span style={{ color: '#777', marginLeft: 8 }}>— {opt.desc}</span>
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* ── Section 4: Client Access ── */}
                <div className="tos-au-section">
                    <div className="tos-au-section-header">
                        <div className="tos-au-section-title">Client Access</div>
                        <div className="tos-au-section-desc">
                            {newUserIsTransit
                                ? 'Select which client environments this Transit Internal user can access. They can access multiple clients and will not appear in client admin lists.'
                                : 'Select the client this user belongs to. Client users can only belong to one client and will appear in that client\'s admin user list. Role and group assignments are managed by client admins.'}
                        </div>
                    </div>
                    <div className="tos-au-section-body">
                        <div className="tos-au-client-list">
                            {clients.length === 0 ? (
                                <div className="tos-none-label" style={{ padding: '12px 0' }}>No clients available.</div>
                            ) : clients.map(client => (
                                <div key={client.name} className="tos-au-client-row">
                                    {newUserIsTransit ? (
                                        /* Multi-select checkbox for Transit users */
                                        <label className="tos-au-client-label">
                                            <input
                                                type="checkbox"
                                                className="tos-au-checkbox"
                                                checked={newUserClientChecks.includes(client.name)}
                                                onChange={() => toggleClientCheck(client.name)}
                                            />
                                            <span className="tos-au-client-name">{client.name}</span>
                                        </label>
                                    ) : (
                                        /* Single-select radio for Client users */
                                        <label className="tos-au-client-label">
                                            <input
                                                type="radio"
                                                className="tos-au-radio"
                                                name="clientAccess"
                                                value={client.name}
                                                checked={newUserClientRadio === client.name}
                                                onChange={() => setNewUserClientRadio(client.name)}
                                            />
                                            <span className="tos-au-client-name">{client.name}</span>
                                        </label>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* ── Form Actions ── */}
                <div className="tos-form-actions" style={{ marginTop: 24, paddingBottom: 32 }}>
                    <button
                        type="button"
                        className="tos-btn-cancel"
                        onClick={() => { setUserView('list'); resetAddUserForm(); }}
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        className={`tos-btn-create ${isAddUserFormValid ? 'valid' : ''}`}
                        disabled={addUserLoading || !isAddUserFormValid}
                    >
                        {addUserLoading ? 'Creating...' : 'Create User'}
                    </button>
                </div>
            </form>
        </div>
    );

    /* ── Configure Client Sub-view (Screen 5 - Read-only) ── */
    const renderConfigureView = () => {
        const client = configureClient;
        if (!client) return null;

        return (
            <div className="tos-subview tos-configure-view">
                <button
                    className="tos-back-btn"
                    onClick={() => { setView('list'); setConfigureClient(null); }}
                >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                        <path d="M19 12H5M5 12L12 19M5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                </button>

                <h2 className="tos-subview-title">{configClientName || client.name}</h2>
                <p className="tos-subview-subtitle">Configure applications and features for this client</p>

                {configLoading ? (
                    <div className="tos-loading">Loading configuration…</div>
                ) : (
                    <div className="tos-configure-content">

                        {/* ── Client Details ── */}
                        <div className="tos-config-section">
                            <div className="tos-config-section-header">
                                <div className="tos-config-section-title">Client Details</div>
                                <div className="tos-config-section-desc">Basic information for this client organization.</div>
                            </div>
                            <div className="tos-form-group">
                                <label className="tos-label">Client Name {isSuperAdminUser && <span className="tos-asterisk">*</span>}</label>
                                <input
                                    className="tos-input"
                                    type="text"
                                    value={configClientName}
                                    onChange={e => isSuperAdminUser && setConfigClientName(e.target.value)}
                                    placeholder="Enter client name"
                                    readOnly={!isSuperAdminUser}
                                    style={!isSuperAdminUser ? { cursor: 'default', opacity: 0.7 } : {}}
                                />
                                <span className="tos-input-hint">The display name for this client organization</span>
                            </div>
                        </div>

                        {/* ── Service Principals ── */}
                        <div className="tos-config-section">
                            <div className="tos-config-section-header">
                                <div className="tos-config-section-title">Service Principals (SPN)</div>
                                <div className="tos-config-section-desc">Databricks service principals assigned to this client. Select all that apply.</div>
                            </div>
                            {allSpns.length === 0 ? (
                                <div className="tos-config-empty">No service principals available.</div>
                            ) : (
                                <div style={{ pointerEvents: isSuperAdminUser ? 'auto' : 'none', opacity: isSuperAdminUser ? 1 : 0.6 }}>
                                    <CustomDropdown
                                        value={configSpns}
                                        onChange={isSuperAdminUser ? setConfigSpns : () => {}}
                                        options={allSpns}
                                        placeholder="Select service principals"
                                        searchable={true}
                                        multi={true}
                                    />
                                    {configSpns.length > 0 && (
                                        <div style={{ marginTop: '8px', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                            {configSpns.map(spn => (
                                                <span key={spn} style={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: '6px',
                                                    padding: '3px 8px 3px 10px',
                                                    backgroundColor: 'rgba(96,165,250,0.1)',
                                                    border: '1px solid rgba(96,165,250,0.3)',
                                                    borderRadius: '12px',
                                                    fontSize: '12px',
                                                    color: '#60A5FA'
                                                }}>
                                                    {spn.replace(/^spn_/i, '')}
                                                    {isSuperAdminUser && (
                                                        <button
                                                            type="button"
                                                            onClick={() => setConfigSpns(prev => prev.filter(s => s !== spn))}
                                                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#60A5FA', fontSize: '14px', lineHeight: '1', padding: '0', display: 'flex', alignItems: 'center' }}
                                                        >×</button>
                                                    )}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* ── Application Access ── */}
                        <div className="tos-config-section">
                            <div className="tos-config-section-header">
                                <div className="tos-config-section-title">Application Access</div>
                                <div className="tos-config-section-desc">Enable or disable applications for this client. Controls visibility on LaunchPad.</div>
                            </div>
                            <div className="tos-app-access-list">

                                {/* Data Studio — toggleable */}
                                <div className="tos-app-access-row">
                                    <div className="tos-app-access-info">
                                        <div className="tos-app-access-name">Data Studio</div>
                                        <div className="tos-app-access-desc">PowerBI data analytics platform</div>
                                    </div>
                                    <label className={`tos-toggle ${!isSuperAdminUser ? 'tos-toggle-disabled' : ''}`}>
                                        <input
                                            type="checkbox"
                                            checked={configDataStudio}
                                            onChange={e => isSuperAdminUser && setConfigDataStudio(e.target.checked)}
                                            disabled={!isSuperAdminUser}
                                        />
                                        <span className="tos-toggle-slider"></span>
                                    </label>
                                </div>

                                {/* AI Studio — always on, read-only */}
                                <div className="tos-app-access-row">
                                    <div className="tos-app-access-info">
                                        <div className="tos-app-access-name">AI Studio</div>
                                        <div className="tos-app-access-desc">AI-powered features and Genies</div>
                                    </div>
                                    <label className="tos-toggle tos-toggle-disabled">
                                        <input type="checkbox" checked={true} disabled />
                                        <span className="tos-toggle-slider"></span>
                                    </label>
                                </div>

                            </div>
                        </div>

                        {/* ── Licensed Features ── */}
                        <div className="tos-config-section">
                            <div className="tos-config-section-header">
                                <div className="tos-config-section-title">Licensed Features</div>
                                <div className="tos-config-section-desc">Specific features licensed within each application. Features are only accessible if the parent application is licensed.</div>
                            </div>

                            {/* Data Studio licensed features */}
                            <div className="tos-licensed-app-block">
                                <div className="tos-licensed-app-header">
                                    <div className="tos-licensed-app-name">Data Studio</div>
                                    <div className="tos-licensed-app-count">
                                        {configDashboards.length} of {availableDashboards.length} features licensed
                                    </div>
                                </div>
                                {availableDashboards.length === 0 ? (
                                    <div className="tos-licensed-empty">No Power BI dashboards configured.</div>
                                ) : (
                                    <div className="tos-licensed-features-list">
                                        {availableDashboards.map((dashboard, idx) => {
                                            const isLicensed = configDashboards.includes(dashboard.id);
                                            return (
                                                <React.Fragment key={dashboard.id}>
                                                    {idx > 0 && <div className="tos-licensed-divider" />}
                                                    <div className="tos-licensed-feature-row">
                                                        <div className="tos-licensed-feature-info">
                                                            <div className="tos-licensed-feature-name">{dashboard.name}</div>
                                                            <div className="tos-licensed-feature-desc">{dashboard.description || `Analytics for ${dashboard.name} data`}</div>
                                                        </div>
                                                        <button
                                                            type="button"
                                                            className={`tos-licensed-badge ${isLicensed ? 'licensed' : 'not-licensed'}`}
                                                            onClick={() => {
                                                                if (!isSuperAdminUser) return;
                                                                setConfigDashboards(prev =>
                                                                    isLicensed
                                                                        ? prev.filter(id => id !== dashboard.id)
                                                                        : [...prev, dashboard.id]
                                                                );
                                                            }}
                                                            disabled={!isSuperAdminUser}
                                                            style={!isSuperAdminUser ? { cursor: 'default', opacity: 0.7 } : {}}
                                                        >
                                                            {isLicensed ? 'Licensed' : 'Not Licensed'}
                                                        </button>
                                                    </div>
                                                </React.Fragment>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* ── Save feedback ── */}
                        {configSaveError && (
                            <div className="tos-config-error">{configSaveError}</div>
                        )}
                        {configSaveSuccess && (
                            <div className="tos-config-success">{configSaveSuccess}</div>
                        )}

                    </div>
                )}

                {/* Footer */}
                <div className="tos-configure-footer">
                    {isSuperAdminUser && (
                        <button
                            className="tos-btn-save-config"
                            onClick={handleSaveConfigure}
                            disabled={configSaving}
                        >
                            {configSaving ? 'Saving…' : 'Save Changes'}
                        </button>
                    )}
                    <button
                        className="tos-btn-cancel-config"
                        onClick={() => { setView('list'); setConfigureClient(null); }}
                        disabled={configSaving}
                    >
                        {isSuperAdminUser ? 'Cancel' : 'Back'}
                    </button>
                </div>
            </div>
        );
    };

    /* ── Add Client Sub-view (Screens 8 & 9) ── */
    const renderAddClientView = () => (
        <div className="tos-subview">
            <button
                className="tos-back-btn"
                onClick={() => { setView('list'); setAddClientError(''); setAddClientSuccess(''); }}
            >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M19 12H5M5 12L12 19M5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
            </button>

            <h2 className="tos-subview-title">Add Client</h2>
            <p className="tos-subview-subtitle">
                Create a new client configuration. After creating the client, you'll be able to assign applications and features.
            </p>

            <form className="tos-form-card" onSubmit={handleCreateClient}>
                <div className="tos-form-section-label">Client Information</div>
                <p className="tos-form-section-desc">
                    Enter the basic client details. The Service Principal (SPN) connects the client to the Databricks workspace.
                    <span className="tos-required-note"> Item with an asterisk(*) is required.</span>
                </p>

                {addClientError && <div className="tos-form-error">{addClientError}</div>}
                {addClientSuccess && <div className="tos-form-success">{addClientSuccess}</div>}

                <div className="tos-form-group">
                    <label className="tos-label">Client Name <span className="tos-asterisk">*</span></label>
                    <input
                        className="tos-input"
                        type="text"
                        placeholder=""
                        value={newClientName}
                        onChange={e => setNewClientName(e.target.value)}
                        required
                    />
                    <span className="tos-input-hint">The display name for this client organization</span>
                </div>

                <div className="tos-form-group">
                    <label className="tos-label">Service Principal (SPN) <span className="tos-asterisk">*</span></label>
                    <CustomDropdown
                        value={newClientSpn}
                        onChange={setNewClientSpn}
                        options={allSpns}
                        placeholder="Select a service principal"
                    />
                    {newClientSpn && (
                        <span className="tos-input-hint">This connects the client to the Databricks workspace and determines data access permissions</span>
                    )}
                </div>

                {newClientSpn && (
                    <div className="tos-next-steps">
                        <div className="tos-next-steps-title">Next Steps</div>
                        <p className="tos-next-steps-desc">After creating this client, you'll be able to:</p>
                        <ul className="tos-next-steps-list">
                            <li>Enable applications (Data Studio, AI Studio)</li>
                            <li>Assign specific features within each application</li>
                            <li>Configure SCM ID and sync with Databricks</li>
                            <li>Create and manage users for this client</li>
                        </ul>
                    </div>
                )}

                <div className="tos-form-actions">
                    <button
                        type="button"
                        className="tos-btn-cancel"
                        onClick={() => { setView('list'); setAddClientError(''); setAddClientSuccess(''); }}
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        className="tos-btn-create"
                        disabled={addClientLoading}
                    >
                        {addClientLoading ? 'Creating...' : 'Create Client'}
                    </button>
                </div>
            </form>
        </div>
    );

    /* ── Clients List View ── */
    const renderClientsTab = () => {
        if (view === 'add') return renderAddClientView();
        if (view === 'configure') return renderConfigureView();

        return (
            <>
                <div className="tos-tab-header">
                    <h1 className="tos-tab-title">Client Management</h1>
                </div>

                <div className="tos-actions-row">
                    <div className="tos-search-wrap">
                        <svg className="tos-search-icon" width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M7 12A5 5 0 1 0 7 2a5 5 0 0 0 0 10zM14 14l-3-3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                        </svg>
                        <input
                            className="tos-search"
                            type="text"
                            placeholder="Search by client name or SPN"
                            value={clientSearch}
                            onChange={e => { setClientSearch(e.target.value); setClientPage(1); }}
                        />
                    </div>
                    <button
                        onClick={handleSyncDatabricksSpns}
                        disabled={spnSyncing}
                        style={{
                            display: 'flex', alignItems: 'center', gap: '6px',
                            padding: '8px 14px', borderRadius: '8px', fontSize: '13px',
                            fontWeight: 600, cursor: spnSyncing ? 'not-allowed' : 'pointer',
                            background: spnSyncing ? '#94a3b8' : '#2563eb',
                            color: '#fff', border: 'none', whiteSpace: 'nowrap'
                        }}
                    >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="23 4 23 10 17 10"/>
                            <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
                        </svg>
                        {spnSyncing ? 'Syncing…' : 'Sync SPNs from Databricks'}
                    </button>
                    {isSuperAdminUser && (
                        <button
                            className="tos-btn-add"
                            onClick={() => { setView('add'); setAddClientError(''); setAddClientSuccess(''); }}
                        >
                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                                <path d="M7 1v12M1 7h12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                            </svg>
                            Add Client
                        </button>
                    )}
                </div>

                {clientsLoading ? (
                    <div className="tos-loading">Loading clients…</div>
                ) : (
                    <>
                        <div className="tos-table-wrap">
                            <table className="tos-table">
                                <thead>
                                    <tr>
                                        <th>Client Name</th>
                                        <th>Service Principal (SPN)</th>
                                        <th>Applications</th>
                                        <th>Status</th>
                                        <th>Users</th>
                                        <th>Created Date</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {pagedClients.length === 0 ? (
                                        <tr>
                                            <td colSpan={7} className="tos-empty-cell">
                                                {clientSearch ? 'No clients match your search.' : 'No clients yet. Click "+ Add Client" to create one.'}
                                            </td>
                                        </tr>
                                    ) : pagedClients.map(client => {
                                        const apps = getClientApps(client);
                                        const status = getClientStatus(client);
                                        const userCount = clientUserCounts[client.name] || client.userCount || 0;
                                        return (
                                            <tr key={client.id || client.name}>
                                                <td className="tos-td-name">{client.name}</td>
                                                <td className="tos-td-spn">
                                                    {(() => {
                                                        const spns = clientSpnsMap[client.name] || [];
                                                        return spns.length > 0 ? (
                                                            <div className="tos-spn-tags">
                                                                {spns.map(spn => (
                                                                    <span key={spn} className="tos-spn-tag">
                                                                        {spn.replace(/^spn_/i, '')}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        ) : '—';
                                                    })()}
                                                </td>
                                                <td>
                                                    <div className="tos-app-tags">
                                                        {apps.map(app => (
                                                            <span key={app} className={`tos-app-tag ${app === 'Data Studio' ? 'data-studio' : 'ai-studio'}`}>
                                                                {app}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </td>
                                                <td>
                                                    <span className={`tos-client-status ${status === 'Active' ? 'active' : 'inactive'}`}>
                                                        <span className="tos-status-dot" />
                                                        {status}
                                                    </span>
                                                </td>
                                                <td className="tos-td-center">{userCount}</td>
                                                <td className="tos-td-date">{fmtDate(client.createdAt || client.created_at)}</td>
                                                <td>
                                                    <button
                                                        className="tos-btn-configure"
                                                        onClick={() => handleOpenConfigure(client)}
                                                    >
                                                        Configure
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        <Pagination
                            currentPage={clientPage}
                            totalPages={totalClientPages}
                            onPageChange={setClientPage}
                            totalItems={filteredClients.length}
                            itemsPerPage={CLIENTS_PER_PAGE}
                            itemLabel="Clients"
                        />
                    </>
                )}
            </>
        );
    };

    /* ── Users Tab (Screens 10-21) ── */
    const renderUsersTab = () => {
      if (userView === 'add') return renderAddUserView();
      if (userView === 'edit') return renderEditUserView();
      return (
        <>
            <div className="tos-tab-header">
                <h1 className="tos-tab-title">User Management</h1>
            </div>

            {/* Info Banner */}
            <div className="tos-info-banner">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0, marginTop: 1 }}>
                    <circle cx="8" cy="8" r="7" stroke="#60A5FA" strokeWidth="1.5"/>
                    <path d="M8 5v3.5M8 11h.01" stroke="#60A5FA" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
                <span>Manage all users across clients. Transit Internal users can access multiple clients and don't appear in client admin lists. Client Users are tied to a single client.</span>
            </div>

            {/* Filter Tabs + Actions Row */}
            <div className="tos-users-controls">
                <div className="tos-user-filter-tabs">
                    {[
                        { key: 'all', label: 'All Users', count: allFilteredUsers.length },
                        { key: 'transit', label: 'Transit Users', count: transitUsers.length },
                        { key: 'client', label: 'Client Users', count: clientUsers.length },
                    ].map(tab => (
                        <button
                            key={tab.key}
                            className={`tos-user-tab ${userTab === tab.key ? 'active' : ''}`}
                            onClick={() => { setUserTab(tab.key); setUserPage(1); }}
                        >
                            {tab.label}
                            <span className={`tos-user-tab-count ${userTab === tab.key ? 'active' : ''}`}>
                                {tab.count}
                            </span>
                        </button>
                    ))}
                </div>
            </div>

            {/* Search + Add User */}
            <div className="tos-actions-row" style={{ marginTop: 16 }}>
                <div className="tos-search-wrap">
                    <svg className="tos-search-icon" width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M7 12A5 5 0 1 0 7 2a5 5 0 0 0 0 10zM14 14l-3-3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                    </svg>
                    <input
                        className="tos-search"
                        type="text"
                        placeholder="Search by name or email"
                        value={userSearch}
                        onChange={e => { setUserSearch(e.target.value); setUserPage(1); }}
                    />
                </div>
                {canEditUsers && (
                    <button className="tos-btn-add" onClick={() => { setUserView('add'); resetAddUserForm(); }}>
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                            <path d="M7 1v12M1 7h12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                        Add User
                    </button>
                )}
            </div>

            {usersLoading ? (
                <div className="tos-loading">Loading users…</div>
            ) : (
                <>
                    <div className="tos-table-wrap">
                        <table className="tos-table">
                            <thead>
                                <tr>
                                    <th>Name & Email</th>
                                    <th>Type</th>
                                    <th>Role</th>
                                    <th>Client Access</th>
                                    <th>Created Date</th>
                                    <th>Last Active</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {pagedUsers.length === 0 ? (
                                    <tr>
                                        <td colSpan={7} className="tos-empty-cell">
                                            {userSearch ? 'No users match your search.' : 'No users found.'}
                                        </td>
                                    </tr>
                                ) : pagedUsers.map(user => {
                                    const email = user.mail || user.userPrincipalName;
                                    const assignedClients = userClientsMap[email] || [];
                                    const isUpdating = userRoleUpdating === email;
                                    const userType = getUserType(user);
                                    const MAX_SHOWN = 2;
                                    const visibleClients = assignedClients.slice(0, MAX_SHOWN);
                                    const overflowCount = assignedClients.length - MAX_SHOWN;
                                    return (
                                        <tr key={user.id || email} className={user.is_active === false ? 'tos-user-row-inactive' : ''}>
                                            {/* Name & Email combined */}
                                            <td className="tos-td-name-email">
                                                <div className="tos-user-display-name">
                                                    {user.displayName || '—'}
                                                    {user.is_active === false && (
                                                        <span className="tos-user-deactivated-badge">Deactivated</span>
                                                    )}
                                                </div>
                                                <div className="tos-user-display-email">{email}</div>
                                            </td>
                                            {/* Type Badge */}
                                            <td>
                                                <span className={`tos-type-badge ${userType === 'transit' ? 'transit' : 'client'}`}>
                                                    {userType === 'transit' ? 'Transit Internal' : 'Client User'}
                                                </span>
                                            </td>
                                            {/* Role */}
                                            <td>
                                                <span className={getRoleBadgeClass(user.role)}>
                                                    {getRoleLabel(user.role)}
                                                </span>
                                            </td>
                                            {/* Client Access */}
                                            <td>
                                                {assignedClients.length > 0 ? (
                                                    <div className="tos-client-tags">
                                                        {visibleClients.map(c => (
                                                            <span key={c} className="tos-client-mini-tag">{c}</span>
                                                        ))}
                                                        {overflowCount > 0 && (
                                                            <span className="tos-client-mini-tag overflow">+{overflowCount}</span>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <span className="tos-none-label">—</span>
                                                )}
                                            </td>
                                            {/* Created Date */}
                                            <td className="tos-td-date">{fmtDate(user.createdDateTime || user.createdAt)}</td>
                                            {/* Last Active */}
                                            <td className="tos-td-date">{fmtDateTime(user.lastActivity || user.last_activity || user.lastLogin) || '—'}</td>
                                            {/* Actions */}
                                            <td>
                                                {canEditUsers ? (
                                                    <button
                                                        className="tos-btn-configure"
                                                        onClick={() => openEditUser(user)}
                                                        disabled={isUpdating}
                                                    >
                                                        Edit
                                                    </button>
                                                ) : (
                                                    <span className="tos-none-label">—</span>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    <Pagination
                        currentPage={userPage}
                        totalPages={totalUserPages}
                        onPageChange={setUserPage}
                        totalItems={tabUsers.length}
                        itemsPerPage={USERS_PER_PAGE}
                        itemLabel="Users"
                    />
                </>
            )}

        </>
      );
    };

    /* ─────────────────────────────────────────────────────────────── */
    /* ROOT RENDER                                                      */
    /* ─────────────────────────────────────────────────────────────── */
    return (
        <div className="tos-layout">
            {/* Sidebar */}
            <aside className="tos-sidebar">
                <div className="tos-sidebar-brand">
                    <div className="tos-sidebar-logo">
                        <img src="/TTLogo.png" alt="Transit Technologies" style={{ height: 22 }} />
                        <span className="tos-data-ai-badge">Data AI</span>
                    </div>
                    <div className="tos-super-admin-pill">
                        {isSuperAdminUser ? 'Super Admin' : 'Admin'}
                    </div>
                </div>

                <div className="tos-sidebar-label">Settings</div>

                <nav className="tos-sidebar-nav">
                    <button
                        className={`tos-sidebar-btn ${activeTab === 'clients' ? 'active' : ''}`}
                        onClick={() => { setActiveTab('clients'); setView('list'); }}
                    >
                        Clients
                    </button>
                    <button
                        className={`tos-sidebar-btn ${activeTab === 'users' ? 'active' : ''}`}
                        onClick={() => setActiveTab('users')}
                    >
                        Users
                    </button>
                </nav>
            </aside>

            {/* Main */}
            <main className="tos-main">
                {activeTab === 'clients' && renderClientsTab()}
                {activeTab === 'users' && renderUsersTab()}
            </main>
        </div>
    );
};

export default AdminTOSDashboard;

